import type { Message } from '../../api/types'
import { Icon } from '../../components/Icon'
import { userName } from '../../lib/channels'
import { replyBotName } from '../../lib/posting'
import { useConversation } from './ConversationContext'
import type { MessageRules } from './messageRules'
import { usePrivateReply } from './privateReply'

interface Props {
  message: Message
  rules: MessageRules
  pickerOpen: boolean
  onTogglePicker: () => void
  sheetOpen: boolean
  /** The message sheet: every other action, and reactions with any emoji. */
  onOpenSheet: (opener: HTMLElement) => void
}

export function MessageTools({ message, rules, pickerOpen, onTogglePicker, sheetOpen, onOpenSheet }: Props) {
  const { me, users, channel, startReply } = useConversation()
  const replyPrivately = usePrivateReply()
  const whose = message.authorId === me.id ? 'your message' : `${userName(users, message.authorId)}'s message`

  return (
    <span className="message-tools">
      {rules.reply ? (
        <button type="button" className="icon-button" aria-label="Reply" title="Reply" onClick={() => startReply(message)}>
          <Icon name="reply" />
        </button>
      ) : null}
      {rules.replyBot ? (
        <button
          type="button"
          className="icon-button"
          aria-label="Reply privately"
          title={`Reply privately to ${replyBotName(rules.replyBot, users)}`}
          onClick={() => void replyPrivately(channel.id, message.id)}
        >
          <Icon name="reply" />
        </button>
      ) : null}
      {rules.react ? (
        <button
          type="button"
          className="icon-button"
          aria-label="Add a reaction"
          title="Add a reaction"
          aria-expanded={pickerOpen}
          onClick={onTogglePicker}
        >
          <Icon name="smile" />
        </button>
      ) : null}
      <button
        type="button"
        className="icon-button more-button"
        aria-label="More message actions"
        title="More"
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
        onClick={(e) => onOpenSheet(e.currentTarget)}
      >
        <Icon name="more" />
      </button>
      {/* Touch screens hide the icons (swipe and hold replace them); screen readers and keyboards keep this. */}
      <button
        type="button"
        className="message-actions-button"
        aria-label={`Actions for ${whose}`}
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
        onClick={(e) => onOpenSheet(e.currentTarget)}
      >
        Actions
      </button>
    </span>
  )
}
